import { ValidationPattern } from 'helpers/form';
import { useOnFirstRender } from 'hooks';
import _ from 'lodash';
import { RuleCondition, RuleConditions, RuleValueType, RuleValueTypes } from 'models';
import React from 'react';
import { IconButton } from 'ui/components/shared';
import { Container, Label, Select, TextInput, ToggleSwitch } from 'ui/components/styled';

const defaultRules = {
  passing: [
    { condition: RuleCondition.Subject.contains, value: '', valueType: RuleValueType.String },
  ],
  failing: [
    { condition: RuleCondition.Subject.contains, value: '', valueType: RuleValueType.String },
  ],
  warning: [
    { condition: RuleCondition.Subject.contains, value: '', valueType: RuleValueType.String },
  ],
};

export const JobRules = ({
  rules,
  rulesLogic = {
    passing: 'and',
    failing: 'and',
    warning: 'and',
  },
  onRulesChange,
  onRulesLogicChange,
  required = true,
  disabled,
  labelProps,
  plusButtonProps,
}) => {
  const [rulesState, setRulesState] = React.useState(
    rules && Object.keys(rules).includes(...Object.keys(defaultRules))
      ? rules
      : _.cloneDeep(defaultRules)
  );

  // const [rulesLogicState, setRulesLogicState] = React.useState(
  //   rulesLogic || {
  //     passing: 'and',
  //     failing: 'and',
  //     warning: 'and',
  //   }
  // );

  const [hasFocus, setHasFocus] = React.useState(false);
  const isFirstRender = useOnFirstRender();

  React.useEffect(() => {
    if (!isFirstRender) {
      setRulesState(rules);
    }
  }, [rules]);

  const handleRuleChange = (e) => {
    const {
      name,
      value,
      dataset: { type, index },
    } = e.target;
    rulesState[type][index][name] = value;
    if (onRulesChange) {
      onRulesChange(rulesState);
    } else {
      setRulesState({ ...rulesState });
    }
  };

  const addRule = (type) => {
    setHasFocus(true);
    const rules = {
      ...rulesState,
      [type]: [
        ...rulesState[type],
        { condition: RuleCondition.Subject.contains, value: '', valueType: RuleValueType.String },
      ],
    };
    if (onRulesChange) {
      onRulesChange(rules);
    } else {
      setRulesState(rules);
    }
  };

  const removeRule = (type, index) => {
    const rules = {
      ...rulesState,
      [type]: rulesState[type].filter((_, i) => i !== index),
    };
    if (onRulesChange) {
      onRulesChange(rules);
    } else {
      setRulesState(rules);
    }
  };

  const handleRulesLogicChange = ({ currentTarget: { name, checked } }) => {
    onRulesLogicChange({ ...rulesLogic, [name]: checked ? 'and' : 'or' });
  };

  return [
    { label: 'Passing message', type: 'passing' },
    { label: 'Failing message', type: 'failing' },
    { label: 'Warning message', type: 'warning' },
  ].map(({ label, type }, i) => (
    <Container key={i} height="auto" data-cy={type}>
      <Label color="tertiary" fontWeight="bold" mb="7px" {...labelProps}>
        {label}
      </Label>
      <ToggleSwitch
        data-cy={`${type}-logic`}
        labelRight={
          rulesLogic[type] === 'and'
            ? `All rules required for ${type === 'passing' ? 'success' : type}`
            : `Any rule required for ${type === 'passing' ? 'success' : type}`
        }
        checked={rulesLogic[type] === 'and'}
        isLabelColored
        onChange={handleRulesLogicChange}
        name={type}
      />
      <Container
        height="auto"
        maxHeight="192px"
        overflow={rulesState[type]?.length < 5 ? 'initial' : 'auto'}
        pt="3px"
        pl="3px"
        mb="xxs"
        mt="xxs"
      >
        {rulesState[type]?.map((rule, index) => (
          <Container key={index} flexDirection="row" alignItems="flex-end" gap="xs" pb="xs">
            <Select
              testId="value-type"
              placeholder="Value type"
              caretColor="primary"
              variant="minimal"
              width="auto"
              fitSize
              options={RuleValueTypes}
              value={rule.valueType}
              onChange={(option) =>
                handleRuleChange({
                  target: {
                    name: 'valueType',
                    dataset: { type, index },
                    value: option.value,
                  },
                })
              }
              disabled={disabled}
              required={required}
            />
            <Select
              testId="condition"
              placeholder="Condition"
              caretColor="primary"
              variant="minimal"
              width="auto"
              fitSize
              options={RuleConditions}
              value={rule.condition}
              onChange={(option) =>
                handleRuleChange({
                  target: {
                    name: 'condition',
                    dataset: { type, index },
                    value: option.value,
                  },
                })
              }
              disabled={disabled}
              required={required}
            />
            <TextInput
              name="value"
              data-index={index}
              data-type={type}
              variant="minimal"
              placeholder="Value"
              value={rule.value}
              pattern={ValidationPattern.any}
              onChange={handleRuleChange}
              autoFocus={hasFocus}
              disabled={disabled}
              required={required}
              width="auto"
              flexGrow="1"
            />
            <Container width="auto" height="auto">
              <IconButton
                variant="transparent"
                src="/assets/img/icons/close-red.svg"
                alt="remove"
                size="14px"
                opacity="0.8"
                opacityOnHover="1"
                mb="xs"
                onClick={() => removeRule(type, index)}
                disabled={disabled}
              />
            </Container>
          </Container>
        ))}
      </Container>
      <PlusButton onClick={() => addRule(type)} disabled={disabled} {...plusButtonProps} />
    </Container>
  ));
};

const PlusButton = (props) => (
  <Container alignItems="center">
    <IconButton
      data-testid="rule-plus"
      icon="plus-white"
      iconProps={{ size: '12px' }}
      boxShadow="none"
      bg="primary"
      width="80px"
      height="30px"
      borderRadius="3px"
      {...props}
    />
  </Container>
);
